import React, { useEffect, useState } from "react";
import axiosInstance from "../../services/auth";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import { useCombinedContext } from "../../context/CombinedContext";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Link } from "react-router-dom";

const StockOutConsolidatedReport = () => {
  const [reportData, setReportData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { selectedCompany } = useCombinedContext();
  const companyId = selectedCompany?._id;

  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));
  const userRole = authData?.role;

  const fetchConsolidatedReport = async () => {
    if (!companyId) return;
    if (!fromDate || !toDate) {
      setReportData([]);
      setFilterData([]);
      return;
    }

    try {
      setLoading(true);
      const params = { start_date: fromDate, end_date: toDate };
      const response = await axiosInstance.get(
        `/logistics/stockout/consolidated/${companyId}`,
        { params }
      );
      const data = response.data.data || [];
      setReportData(data);
      setFilterData(data);
      setCurrentPage(0);
    } catch (error) {
      toast.error("Error fetching consolidated stockout report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && fromDate && toDate) {
      fetchConsolidatedReport();
    }
  }, [companyId, fromDate, toDate]);

  const handleSearchChange = (e) => {
    const text = e.target.value.toLowerCase();
    setSearchText(text);
    const filtered = reportData.filter(
      (item) =>
        String(item.stockName ?? "").toLowerCase().includes(text) ||
        String(item.partNo ?? "").toLowerCase().includes(text) ||
        String(item.description ?? "").toLowerCase().includes(text)
    );
    setFilterData(filtered);
    setCurrentPage(0);
  };

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  const currentItems = filterData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );
  const pageCount = Math.ceil(filterData.length / itemsPerPage);

  const getMonthYear = () => {
    if (!fromDate) return { month: "-", year: "-" };
    const d = new Date(fromDate);
    const month = d.toLocaleString("default", { month: "long" });
    const year = d.getFullYear();
    return { month, year };
  };

  const { month, year } = getMonthYear();

  const handleExport = () => {
    if (!filterData || filterData.length === 0) {
      toast.info("No data to export");
      return;
    }

    const sheetData = [];
    const merges = [];

    const reportDate = new Date().toLocaleDateString();
    sheetData.push([`Report Generated Date: ${reportDate}`]);
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 8 } });

    sheetData.push([
      `Period: ${fromDate || "-"}  to  ${toDate || "-"}`,
      "", "", "", "", "", "", "", "",
    ]);
    merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 8 } });

    sheetData.push([
      "Sl No",
      "Month",
      "Year",
      "Part No",
      "Stock Name",
      "Description",
      "Available Qty",
      "Used Qty",
      "Remaining Qty",
    ]);

    filterData.forEach((item, index) => {
      sheetData.push([
        index + 1,
        item.month || month,
        item.year || year,
        item.partNo || "-",
        item.stockName || "-",
        item.description || "-",
        item.availableQty ?? 0,
        item.usedQty ?? 0,
        item.remainingQty ?? 0,
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet["!merges"] = merges;
    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 14 },
      { wch: 8 },
      { wch: 14 },
      { wch: 28 },
      { wch: 30 },
      { wch: 14 },
      { wch: 12 },
      { wch: 14 },
    ];

    // Bold header row
    Object.keys(worksheet).forEach((cellKey) => {
      if (cellKey[0] === "!") return;
      const cell = worksheet[cellKey];
      const addr = XLSX.utils.decode_cell(cellKey);
      if (addr.r === 2) {
        cell.s = { font: { bold: true } };
      }
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "StockOut Consolidated");
    XLSX.writeFile(
      workbook,
      `stockout_consolidated_${fromDate}_${toDate}.xlsx`
    );
  };

  return (
    <div>
      <ToastContainer />
      <Link to="/logistics/stockoutlist">
        <button className="linkBtn mb-3">
          <i className="bi bi-arrow-left me-2"></i>Back to Stock Out List
        </button>
      </Link>

      <div className="mb-4 headStyle d-flex justify-content-between align-items-center">
        <h4 className="mb-0">StockOut Consolidated Report</h4>
      </div>

      {/* Filter Panel */}
      <div
        className="d-flex flex-wrap mt-3 align-items-center gap-3"
        style={{
          padding: "16px",
          boxShadow: "0px 0px 10px 0px #e0e0e0",
          background: "white",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label htmlFor="fromDate" style={{ width: "-webkit-fill-available" }}>
            From Date
          </label>
          <input
            className="form-control"
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setCurrentPage(0);
            }}
            style={{ border: "1px solid #1E2A5E", borderRadius: "8px" }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label htmlFor="toDate" style={{ width: "-webkit-fill-available" }}>
            To Date
          </label>
          <input
            className="form-control"
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setCurrentPage(0);
            }}
            style={{ border: "1px solid #1E2A5E", borderRadius: "8px" }}
          />
        </div>
      </div>

      {/* Table section */}
      <div className="headStyle mt-4">
        <div className="d-flex mt-2 flex-wrap align-items-center gap-3 justify-content-between">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div>
              <input
                className="form-control"
                type="search"
                placeholder="Search by name, part no, description..."
                style={{
                  maxWidth: "320px",
                  border: "1px solid #1E2A5E",
                  borderRadius: "8px",
                }}
                value={searchText}
                onChange={handleSearchChange}
              />
            </div>
            <div className="d-flex align-items-center">
              <label className="me-2 pt-1">Count</label>
              <select
                className="form-select no-arrow"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(0);
                }}
                style={{ width: "auto" }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
                <option value="25">25</option>
                <option value="30">30</option>
              </select>
            </div>
          </div>
          {["admin", "superadmin"].includes(userRole) && (
            <div>
              <button className="button-2 exxportBtn" onClick={handleExport}>
                Export to Excel
              </button>
            </div>
          )}
        </div>

        <div
          className="my-3 rounded table-responsive-sm"
          style={{ overflowX: "auto" }}
        >
          <table id="stockout-consolidated-table" className="table table-bordered mb-0">
            <thead className="t_head">
              <tr className="text-center align-middle">
                <th>Sl No</th>
                <th>Month</th>
                <th>Year</th>
                <th>Part No</th>
                <th>Stock Name</th>
                <th>Description</th>
                <th>Available Qty</th>
                <th>Used Qty</th>
                <th>Remaining Qty</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9">
                    <div className="d-flex justify-content-center m-3">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : !fromDate || !toDate ? (
                <tr>
                  <td colSpan="9">
                    <div className="d-flex justify-content-center m-2">
                      <h6>Please select both From Date and To Date</h6>
                    </div>
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="9">
                    <div className="d-flex justify-content-center m-2">
                      <h6>Data Not Found</h6>
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((item, index) => (
                  <tr key={index}>
                    <td className="text-center">
                      {currentPage * itemsPerPage + index + 1}
                    </td>
                    <td className="text-center">{item.month || month}</td>
                    <td className="text-center">{item.year || year}</td>
                    <td className="text-center">{item.partNo || "-"}</td>
                    <td>{item.stockName || "-"}</td>
                    <td>{item.description || "-"}</td>
                    <td className="text-end">{item.availableQty ?? 0}</td>
                    <td className="text-end" style={{ color: "#c62828" }}>
                      {item.usedQty ?? 0}
                    </td>
                    <td className="text-end" style={{ color: "#2e7d32", fontWeight: 600 }}>
                      {item.remainingQty ?? 0}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <ReactPaginate
          breakLabel="..."
          nextLabel="next >"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="< previous"
          renderOnZeroPageCount={null}
          containerClassName="pagination justify-content-center pagination-sm"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          activeClassName="active"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          disabledClassName="disabled"
        />
      </div>
    </div>
  );
};

export default StockOutConsolidatedReport;
